import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetCategories } from "../../hooks/categories/useGetCategories";
import { useDeleteCategory } from "../../hooks/categories/useDeleteCategory";
import { useDeleteAllCategories } from "../../hooks/categories/useDeleteAllCategories";
import { useRestoreAllCategories } from "../../hooks/categories/useRestoreAllCategories";
import { useForceDeleteAllCategories } from "../../hooks/categories/useForceDeleteAllCategories";
import { useGetCategoriesForSelect } from "../../hooks/categories/useGetCategoriesForSelect";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import CategoriesTableRow from "./Table/CategoriesTableRow";
import { CategoriesTableColumns } from "./Table/CategoriesTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const CategoriesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("viewAny-category"),
            create: has("create-category"),
            edit: has("update-category"),
            delete: has("delete-category"),
        }),
        [has]
    );

    const navigate = useNavigate();

    // Filter States
    const [trashedFilter, setTrashedFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [parentFilter, setParentFilter] = useState("");

    // CRUD Hooks
    const { deleteCat, isDelete } = useDeleteCategory();
    const { deleteAllCategoriesMutate, isPending: isDeleteAll } = useDeleteAllCategories();
    const { restoreAllCategoriesMutate, isPending: isRestoreAll } = useRestoreAllCategories();
    const { forceDeleteAllCategoriesMutate, isPending: isForceDeleteAll } = useForceDeleteAllCategories();

    const { options: categoryOptions } = useGetCategoriesForSelect();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/categories",
        deleteSingle: deleteCat,
        deleteAll: deleteAllCategoriesMutate,
        restoreSingle: (id, options) => restoreAllCategoriesMutate({ items: [id] }, options),
        restoreAll: restoreAllCategoriesMutate,
        forceDeleteSingle: (id, options) => forceDeleteAllCategoriesMutate({ items: [id] }, options),
        forceDeleteAll: forceDeleteAllCategoriesMutate,
        isArchive: trashedFilter === "only",
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            trashed: trashedFilter === "only" ? "only" : undefined,
            parent_id: parentFilter || undefined,
            is_active: statusFilter !== "" ? statusFilter : undefined,
            sort_by: 'created_at',
            sort_order: order,
        }),
        [page, perPage, search, trashedFilter, parentFilter, statusFilter, order]
    );

    const { isLoading, error, categories, meta } = useGetCategories(params);

    const tableFilters = useMemo(() => [
        {
            key: "parent",
            label: { ar: "الفئة الأب", en: "Parent Category" },
            value: parentFilter,
            onChange: (val) => setParentFilter(val),
            options: [{ value: "", label: { ar: "الكل", en: "All" } }, ...categoryOptions.map(cat => ({
                value: cat.id,
                label: { ar: cat.name?.ar || cat.name?.en, en: cat.name?.en || cat.name?.ar }
            }))]
        },
        {
            key: "status",
            label: { ar: "الحالة", en: "Status" },
            value: statusFilter,
            onChange: (val) => setStatusFilter(val),
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                { value: "1", label: { ar: "نشط", en: "Active" } },
                { value: "0", label: { ar: "غير نشط", en: "Inactive" } },
            ]
        }
    ], [parentFilter, statusFilter, categoryOptions]);

    const columns = useMemo(() => CategoriesTableColumns(isArabic), [isArabic]);

    return (
        <TableLayout
            title={{ ar: "الفئات", en: "Categories" }}
            columns={columns}
            items={categories}
            meta={meta}
            isLoading={isLoading}
            error={error}
            perms={perms}
            filters={tableFilters}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            onSelectAllClick={(e) => handleSelectAllClick(e, categories)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onBulkDelete={handleBulkDelete}
            onBulkRestore={handleBulkRestore}
            isBulkDeleting={isDeleteAll || isForceDeleteAll}
            isBulkRestoring={isRestoreAll}
            isArchive={trashedFilter === "only"}
            onAddClick={() => navigate("/categories/create")}
            addButtonLabel={{ ar: "إضافة فئة", en: "Add Category" }}
            page={page}
            perPage={perPage}
            order={order}
            onRequestSort={handleRequestSort}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            onMenuRestore={handleMenuRestore}
            onArchiveToggle={() => setTrashedFilter(prev => prev === "only" ? "" : "only")}
            isDeletingSingle={isDelete || isForceDeleteAll}
            renderRow={(params) => (
                <CategoriesTableRow
                    {...params}
                    key={params.item.id}
                    category={params.item}
                    perms={perms}
                    onMenuOpen={handleMenuOpen}
                />
            )}
        />
    );
};

export default CategoriesTable;
